import { CSSProperties, memo, useCallback, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { TRACK_USE_ACTUAL_OPTIONS, TRACK_USE_REQUESTED_OPTIONS } from 'utils/constants';

type TTrackUseType = { id: string; name: string };

type TrackUseTypeSelectProps = {
  usageType?: 'requested' | 'actual';
  value?: string;
  onChange: (nextValue: TTrackUseType) => void;
  listStyles?: CSSProperties;
  id?: string;
  name?: string;
  testId?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
};

/**
 * usageType: 'requested' | 'actual' – Only the 'requested' type does show the BIBV option
 */
function TrackUseTypeSelectBase({
  usageType = 'requested',
  id,
  name,
  value,
  onChange,
  listStyles,
  testId,
  placeholder,
  size,
  disabled,
}: TrackUseTypeSelectProps) {
  const options = useMemo(
    () => (usageType === 'requested' ? TRACK_USE_REQUESTED_OPTIONS : TRACK_USE_ACTUAL_OPTIONS),
    [usageType],
  );
  const initialValue = value ? options.find((it) => it.id === value) : null;
  const [selected, setSelected] = useState<TTrackUseType>(initialValue);

  return (
    <Autocomplete
      fullWidth
      size={size}
      key={value}
      id={id}
      disabled={disabled}
      value={selected}
      onChange={(_, nextValue: TTrackUseType) => {
        setSelected(nextValue);
        onChange(nextValue);
      }}
      options={options}
      isOptionEqualToValue={(option, optionValue) => option?.id === optionValue?.id}
      getOptionLabel={(option) => option?.name ?? ''}
      renderInput={(params) => (
        <TextField
          fullWidth
          {...params}
          name={name}
          placeholder={placeholder}
          data-testid={`typeahead-field-${testId || name}`}
        />
      )}
      ListboxProps={{
        style: listStyles,
      }}
    />
  );
}

/**
 * Memoized so that a keystroke in another field of the same form doesn't force this
 * (expensive) MUI Autocomplete to re-render. With many clearances/cues on screen, the
 * unmemoized version re-rendered every select on every keystroke, which made typing laggy.
 * Relies on `value` being a primitive and `onChange` being referentially stable.
 */
export const TrackUseTypeSelect = memo(TrackUseTypeSelectBase);

type FormTrackUseTypeSelectProps = TrackUseTypeSelectProps & {
  onChange?: (nextValue: TTrackUseType) => void;
};

export function FormTrackUseTypeSelect({ name, onChange, ...props }: FormTrackUseTypeSelectProps) {
  const [field] = useField<string>(name);
  const { setFieldValue } = useFormikContext();
  /** Stable across renders (setFieldValue/name are stable) so the memoized select can bail out. */
  const handleChange = useCallback(
    (nextValue: TTrackUseType) => {
      setFieldValue(name, !nextValue ? null : nextValue.id);
      onChange?.(nextValue);
    },
    [setFieldValue, name, onChange],
  );

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <TrackUseTypeSelect value={field.value} onChange={handleChange} {...props} />;
}
